import { LevelType } from 'data/types';
import {
  levelHasPartialDuctwork,
  structureHasPartialDuctworkLevel
} from 'utils/ductwork';
import { isDuctedPathway } from 'utils/project';
import { isLevelConditioned } from 'utils/structure';
import { levelHasSpaces } from 'utils/structure-add-spaces';
import { sortLevelsByVerticalPosition } from 'utils/structure-basics';
import { sortLevelsConditionedFirst } from 'utils/structure-layout-order';
import {
  SpaceDetailsCategory,
  SpaceDetailsGridMode,
  SpaceDetailsSubStep
} from 'utils/structure-space-details-types';

function getLevelsConditionedFirstBottomUp(
  structure: LevelType[]
): LevelType[] {
  return sortLevelsConditionedFirst(sortLevelsByVerticalPosition(structure));
}

function levelHasConditionedSpaces(level: LevelType): boolean {
  return level.sections.some((section) =>
    section.spaces.some((space) => space.conditioned !== false)
  );
}

export function levelNeedsSpaceDetails(level: LevelType): boolean {
  return isLevelConditioned(level) || levelHasConditionedSpaces(level);
}

export function isDuctworkCategoryRelevant(structure: LevelType[]): boolean {
  return isDuctedPathway() && structureHasPartialDuctworkLevel(structure);
}

export function getDuctworkRelevantLevels(structure: LevelType[]): LevelType[] {
  return getLevelsConditionedFirstBottomUp(structure)
    .filter(levelNeedsSpaceDetails)
    .filter(levelHasPartialDuctwork);
}

export function getSpaceDetailLevels(structure: LevelType[]): LevelType[] {
  return getLevelsConditionedFirstBottomUp(structure)
    .filter(levelNeedsSpaceDetails)
    .filter(levelHasSpaces);
}

export function getSpaceDetailsSubSteps(
  structure: LevelType[]
): SpaceDetailsSubStep[] {
  const subSteps: SpaceDetailsSubStep[] = [];
  if (isDuctworkCategoryRelevant(structure)) {
    getDuctworkRelevantLevels(structure).forEach((level) => {
      subSteps.push({ category: 'ductwork', level });
    });
  }
  const detailLevels = getSpaceDetailLevels(structure);
  detailLevels.forEach((level) => {
    subSteps.push({ category: 'windows', level });
  });
  detailLevels.forEach((level) => {
    subSteps.push({ category: 'appliances', level });
  });
  return subSteps;
}

export function getGridModeForSpaceDetailsCategory(
  category: SpaceDetailsCategory
): SpaceDetailsGridMode {
  if (category === 'ductwork') {
    return 'conditioning';
  }
  if (category === 'appliances') {
    return 'appliances';
  }
  return 'summary';
}

export function getSpaceDetailsCategoryLabel(
  category: SpaceDetailsCategory
): string {
  if (category === 'ductwork') {
    return 'Ductwork';
  }
  if (category === 'appliances') {
    return 'Appliances';
  }
  return 'Windows';
}

export function getSpaceDetailsCategories(
  structure: LevelType[]
): SpaceDetailsCategory[] {
  const categories: SpaceDetailsCategory[] = [];
  getSpaceDetailsSubSteps(structure).forEach((subStep) => {
    if (!categories.includes(subStep.category)) {
      categories.push(subStep.category);
    }
  });
  return categories;
}
